import {SureFlowRatingUnitRequest} from '../model/sure-flow-rating-unit-request';
import {UnitRatingRequest} from '../model/unit-rating-request';
import {CoilRequest} from '../model/coil-request';
import {SureFlowRatingCoilRequest} from '../model/sure-flow-rating-coil-request';
import {UnitRatingResponse} from '../model/unit-rating-response';
import {SureFlowRatingUnitResponse} from '../model/sure-flow-rating-unit-response';
import {CoilResponse} from '../model/coil-response';
import {SureFlowRatingCoilResponse} from '../model/sure-flow-rating-coil-response';
import {CircuitResponse} from '../model/circuit-response';
import {SureFlowRatingCircuitResponse} from '../model/sure-flow-rating-circuit-response';
import {SureFlowUnitData} from '../model/sure-flow-unit-data';

export class RatingUnitUtils {

  static convertSureFlowRatingRequest(request: SureFlowRatingUnitRequest): UnitRatingRequest {
    if (!request) {
      return undefined!;
    }
    const converted = new UnitRatingRequest();
    converted.Airflow = request.airflow;
    converted.Altitude = request.altitude;
    converted.Model = request.model;
    converted.Size = request.size;
    converted.MotorConfiguration = request.motorVoltage;
    converted.MotorType = request.motorType;
    converted.PrimaryCoilRequest = RatingUnitUtils.convertSureFlowRatingCoilRequest(request.primaryCoilRequest);
    converted.SecondaryCoilRequest = RatingUnitUtils.convertSureFlowRatingCoilRequest(request.secondaryCoilRequest);
    converted.ElectricHeatRequest = RatingUnitUtils.convertSureFlowRatingCoilRequest(request.electricHeatRequest);
    converted.Filter = request.filter;
    converted.FanSpeed = request.fanSpeed;
    converted.ExternalStaticPressure = request.externalStaticPressure;
    converted.TotalStaticPressure = request.totalStaticPressure;
    converted.IsMetricInput = request.metric;
    return converted;
  }

  private static convertSureFlowRatingCoilRequest(request: SureFlowRatingCoilRequest): CoilRequest {
    if (!request) {
      return undefined!;
    }
    const converted = new CoilRequest();
    converted.EnteringDryBulb = request.enteringDryBulb;
    converted.EnteringWetBulb = request.enteringWetBulb;
    converted.RowCount = request.rowCount;
    converted.TemperatureChange = request.temperatureChange;
    converted.FlowRate = request.flowRate;
    converted.FluidType = request.fluidType;
    converted.EnteringFluidTemperature = request.enteringFluidTemperature;
    converted.ElectricHeatKilowatts = request.electricHeatKilowatts;
    converted.ElectricHeatVoltage = request.electricHeatVoltage;
    converted.GlycolPercentage = request.glycolPercentage;
    converted.isRefrigerant = false;
    converted.isSteam = false;
    converted.isFluid = !request.isElectricHeat;
    converted.isCooling = request.isCooling;
    converted.isElectricHeat = request.isElectricHeat;
    if (converted.isElectricHeat && converted.ElectricHeatKilowatts > 1000) {
      converted.ElectricHeatKilowatts = converted.ElectricHeatKilowatts / 1000;
    }
    return converted;
  }

  static convertRatingResponse(response: UnitRatingResponse): SureFlowRatingUnitResponse {
    if (!response) {
      return undefined!;
    }
    const converted = new SureFlowRatingUnitResponse();
    converted.airflow = response.Airflow;
    converted.circuitResponse = RatingUnitUtils.convertRatingCircuitResponse(response.CircuitResponse);
    converted.alternateCircuit = RatingUnitUtils.convertRatingCircuitResponse(response.AlternateCircuit);
    converted.primaryCoilResponse = RatingUnitUtils.convertRatingCoilResponse(response.PrimaryCoilResponse);
    converted.secondaryCoilResponse = RatingUnitUtils.convertRatingCoilResponse(response.SecondaryCoilResponse);
    converted.electricHeatResponse = RatingUnitUtils.convertRatingCoilResponse(response.ElectricHeatResponse);
    converted.errorMessage = response.ErrorMessage;
    return converted;
  }

  private static convertRatingCoilResponse(response: CoilResponse): SureFlowRatingCoilResponse {
    if (!response) {
      return undefined!;
    }
    const converted = new SureFlowRatingCoilResponse();
    converted.totalCapacity = response.TotalCapacity;
    converted.sensibleCapacity = response.SensibleCapacity;
    converted.leavingDryBulb = response.LeavingDryBulbTemperature;
    converted.leavingWetBulb = response.LeavingWetBulbTemperature;
    converted.leavingFluidTemperature = response.LeavingFluidTemperature;
    converted.fluidFlowRate = response.FluidFlowRate;
    converted.airVelocity = response.AirVelocity;
    converted.numberOfCircuits = response.NumberOfCircuits;
    converted.finsPerInch = response.FinsPerInch;
    return converted;
  }

  private static convertRatingCircuitResponse(response: CircuitResponse): SureFlowRatingCircuitResponse {
    if (!response) {
      return undefined!;
    }
    const converted = new SureFlowRatingCircuitResponse();
    converted.fullLoadAmps = response.FullLoadAmps;
    converted.minimumCircuitAmps = response.MinimumCircuitAmps;
    converted.maximumFuseSize = response.MaximumFuseSize;
    converted.outputPower = response.OutputPower;
    converted.horsepowerValue = response.HorsepowerValue;
    return converted;
  }

  static clearSureFlowRatingIds(unitData: SureFlowUnitData): void {
    if (unitData?.sureFlowRatingUnit) {
      unitData.sureFlowRatingUnit.id = 0;
      if (unitData.sureFlowRatingUnit.request) {
        unitData.sureFlowRatingUnit.request.id = 0;
        if (unitData.sureFlowRatingUnit.request.primaryCoilRequest) {
          unitData.sureFlowRatingUnit.request.primaryCoilRequest.id = 0;
        }
        if (unitData.sureFlowRatingUnit.request.secondaryCoilRequest) {
          unitData.sureFlowRatingUnit.request.secondaryCoilRequest.id = 0;
        }
        if (unitData.sureFlowRatingUnit.request.electricHeatRequest) {
          unitData.sureFlowRatingUnit.request.electricHeatRequest.id = 0;
        }
      }
      if (unitData.sureFlowRatingUnit.response) {
        unitData.sureFlowRatingUnit.response.id = 0;
        if (unitData.sureFlowRatingUnit.response.circuitResponse) {
          unitData.sureFlowRatingUnit.response.circuitResponse.id = 0;
        }
        if (unitData.sureFlowRatingUnit.response.alternateCircuit) {
          unitData.sureFlowRatingUnit.response.alternateCircuit.id = 0;
        }
        if (unitData.sureFlowRatingUnit.response.primaryCoilResponse) {
          unitData.sureFlowRatingUnit.response.primaryCoilResponse.id = 0;
        }
        if (unitData.sureFlowRatingUnit.response.secondaryCoilResponse) {
          unitData.sureFlowRatingUnit.response.secondaryCoilResponse.id = 0;
        }
        if (unitData.sureFlowRatingUnit.response.electricHeatResponse) {
          unitData.sureFlowRatingUnit.response.electricHeatResponse.id = 0;
        }
      }
    }
  }

}
